import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from '@/context/TranslationContext';

const DISMISS_KEY = 'config-go-pwa-install-dismissed';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPwa() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') {
      return;
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);

    if (outcome === 'dismissed') {
      localStorage.setItem(DISMISS_KEY, '1');
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!visible || !deferredPrompt) {
    return null;
  }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[100] mx-auto flex max-w-lg items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 text-gray-900 shadow-2xl sm:left-auto sm:right-6"
      role="dialog"
      aria-label={t('pwa.installTitle')}
    >
      <img
        src={`${process.env.PUBLIC_URL}/config-go-icon.png`}
        alt=""
        className="h-12 w-12 shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900">{t('pwa.installTitle')}</p>
        <p className="text-sm text-gray-600">{t('pwa.installDescription')}</p>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white hover:bg-orange-600"
      >
        <Download className="h-4 w-4" />
        {t('pwa.installButton')}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label={t('pwa.dismiss')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
